import React, { useState } from 'react';
import { Grid, CircularProgress, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { CourseCard, CourseTabs, CourseHeader, EmptyState } from './components';
import Header from 'modules/views/shared/Header';
import { ReactComponent as InformationBlueIcon } from 'assets/icons/InformationBlueIcon.svg';
import Pagination from 'modules/views/shared/Pagination';

import { useCourseList } from './hooks/useCourseList';

import { Course, TabIndex } from './types';
import { useStyles } from './styles';

const Courses = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    workerCourses,
    courseCounts,
    isLoading,
    error,
    activeTab,
    page,
    handleTabChange,
    handlePageChange,
    paginationInfo,
    handleSortChange,
    sortType,
    workerInfo,
    isWorker,
  } = useCourseList();

  const onPageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    handlePageChange(value);
  };

  const getFilteredCourses = (): Course[] => {
    switch (activeTab) {
      case TabIndex.ACTIVE:
        return workerCourses?.active || [];
      case TabIndex.EXPIRED:
        return workerCourses?.expired || [];
      default:
        return [];
    }
  };

  const renderCoursesContent = () => {
    const filteredCourses = getFilteredCourses();

    if (filteredCourses.length === 0) {
      return <EmptyState activeTab={activeTab} />;
    }

    return (
      <Grid container spacing={3} className={classes.coursesGrid} data-testid="courses-grid">
        {filteredCourses.map(course => (
          <Grid item xs={12} sm={6} md={4} key={course.courseId}>
            <CourseCard course={course} workerInfo={workerInfo} isExpired={activeTab === TabIndex.EXPIRED} />
          </Grid>
        ))}
      </Grid>
    );
  };

  const bannerText = isWorker
    ? t('courses.loadingNotice')
    : `${t('common.stableInternetRequired')} ${t('courses.loadingNotice')}`;
  const bannerIcon = isWorker ? <InformationBlueIcon /> : undefined;

  return (
    <div className={`${classes.container} ${classes.containerWithWarning}`}>
      <Header
        styleClass={classes.mainHeader}
        showOnlineWarning={true}
        warningText={bannerText}
        warningIcon={bannerIcon}
      />

      {/* Header */}
      <CourseHeader />

      {/* Tabs */}
      <CourseTabs
        activeTab={activeTab}
        onTabChange={handleTabChange}
        courseCounts={courseCounts}
        onSortChange={handleSortChange}
        sortType={sortType}
      />

      {/* Loading State */}
      {isLoading && (
        <div className={classes.loadingContainer} data-testid="courses-loader">
          <CircularProgress />
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className={classes.errorContainer} data-testid="courses-error">
          <Alert severity="error">{error}</Alert>
        </div>
      )}

      {/* Courses Content */}
      <div className={classes.content} data-testid="courses-content">
        {!isLoading && !error && renderCoursesContent()}
      </div>

      {/* Pagination */}
      {!isLoading && !error && paginationInfo && paginationInfo.totalRecords > 0 && (
        <div className={classes.paginationWrapper}>
          <Pagination
            count={paginationInfo.totalPages}
            page={page}
            onChange={onPageChange}
            siblingCount={1}
            boundaryCount={3}
            testId="courses-pagination"
          />
        </div>
      )}
    </div>
  );
};

export default Courses;
